import React, { useEffect, useMemo, useState } from 'react'
import GraphicsPanel from './GraphicsPanel'
import ShopDesignsPicker from './ShopDesignsPicker'

function ArtworkIcon() {
  return (
    <svg
      aria-hidden="true"
      data-testid="sidebar-artwork-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="8.5" cy="9" r="1.5" />
      <path d="m4 17 4.5-4.5 3.5 3.5 2.5-2.5L20 19" />
    </svg>
  )
}

const TOOLS = [
  { id: 'artwork', legacyTestId: 'graphics', label: 'Artwork', icon: <ArtworkIcon /> },
  { id: 'library', label: 'Shop designs', icon: '\u2605' },
]

export default function Sidebar({
  compact = false,
  images,
  imageUrls,
  activeCanvas,
  placementCounts,
  activeSheetNumber,
  editingDisabled,
  sheetSize,
  uploadErrors,
  uploadingFiles,
  imageConfigs,
  selectedSheetSize,
  onOpenUploadPicker,
  onUploadFiles,
  onImageConfigChange,
  onRemoveImage,
  onRetryImage,
  selectedArtwork,
  selectedCanvasObject,
  onArtworkInstanceConfigChange,
  onAddCopy,
  onRemoveCopy,
  onSelectCanvasObject,
  onPlaceImage,
  deletingImageIds,
  onBackgroundPreview,
  onBackgroundApply,
  vendorArtworks = [],
  vendorArtworksLoading = false,
  vendorArtworksError = null,
  shopDesignsSearch = null,
  onImportVendorArtwork,
  maxImageCount = 50,
}) {
  const [activeTool, setActiveTool] = useState('artwork')
  const [panelOpen, setPanelOpen] = useState(!compact)
  useEffect(() => setPanelOpen(!compact), [compact])
  const activeToolConfig = TOOLS.find(tool => tool.id === activeTool) || TOOLS[0]
  const importedVendorArtworkIds = useMemo(() => new Set(
    images.map(image => image.vendorArtworkPublicId).filter(Boolean).map(String)
  ), [images])
  const atImageLimit = images.length + uploadingFiles.length >= maxImageCount

  return (
    <div
      className={`editor-sidebar-container${panelOpen ? '' : ' editor-sidebar-container--collapsed'}`}
      data-testid="sidebar"
    >
      <div className="sidebar-primary-rail" data-testid="sidebar-primary-rail">
        {TOOLS.map(tool => (
          <button
            key={tool.id}
            className={`sidebar-tool-btn${activeTool === tool.id ? ' sidebar-tool-btn--active' : ''}`}
            onClick={() => {
              setActiveTool(tool.id)
              setPanelOpen(true)
            }}
            title={tool.label}
            aria-label={tool.label}
            aria-expanded={panelOpen && activeTool === tool.id}
            data-testid={`sidebar-tab-${tool.legacyTestId || tool.id}`}
          >
            <span className="sidebar-tool-btn__icon" aria-hidden="true">{tool.icon}</span>
            <span className="sidebar-tool-btn__label">{tool.label}</span>
          </button>
        ))}

        <button
          className="sidebar-collapse-btn"
          onClick={() => setPanelOpen(open => !open)}
          title={panelOpen ? 'Collapse panel' : 'Expand panel'}
          aria-label={panelOpen ? 'Collapse panel' : 'Expand panel'}
          data-testid="sidebar-collapse-btn"
        >
          {panelOpen ? '\u2039' : '\u203A'}
        </button>
      </div>

      {panelOpen && (
        <div className="sidebar-panel" data-testid="sidebar-panel">
          <div className="sidebar-panel__header">
            <h2>{activeToolConfig.label}</h2>
            {compact && (
              <button type="button" onClick={() => setPanelOpen(false)} aria-label="Close artwork panel">
                Back to canvas
              </button>
            )}
          </div>

          {activeTool === 'artwork' && (
            <>
              <GraphicsPanel
                images={images}
                imageUrls={imageUrls}
                activeCanvas={activeCanvas}
                placementCounts={placementCounts}
                activeSheetNumber={activeSheetNumber}
                editingDisabled={editingDisabled}
                sheetSize={sheetSize}
                uploadErrors={uploadErrors}
                uploadingFiles={uploadingFiles}
                imageConfigs={imageConfigs}
                selectedSheetSize={selectedSheetSize}
                selectedArtwork={selectedArtwork}
                selectedCanvasObject={selectedCanvasObject}
                onOpenUploadPicker={onOpenUploadPicker}
                onUploadFiles={onUploadFiles}
                onImageConfigChange={onImageConfigChange}
                onArtworkInstanceConfigChange={onArtworkInstanceConfigChange}
                onRemoveImage={onRemoveImage}
                onRetryImage={onRetryImage}
                onAddCopy={onAddCopy}
                onRemoveCopy={onRemoveCopy}
                onSelectCanvasObject={onSelectCanvasObject}
                onPlaceImage={onPlaceImage}
                deletingImageIds={deletingImageIds}
                onBackgroundPreview={onBackgroundPreview}
                onBackgroundApply={onBackgroundApply}
              />
            </>
          )}

          {activeTool === 'library' && (
            <ShopDesignsPicker
              artworks={vendorArtworks}
              loading={vendorArtworksLoading}
              error={vendorArtworksError}
              importedIds={importedVendorArtworkIds}
              search={shopDesignsSearch}
              atImageLimit={atImageLimit}
              onImport={onImportVendorArtwork}
              compact
              idPrefix="manual-shop-designs"
            />
          )}

        </div>
      )}
    </div>
  )
}
